import { useEffect, useState } from "react";

import { dbFirestore } from "../../firestore";

import type { IssuedTicket } from "../../firestore/types";

/**
 * 指定された複数eventIdのissuedTicketsをリアルタイム同期する
 * enablePersistence()が有効な場合、オフライン時はIndexedDBキャッシュから返す
 */
export const useIssuedTicketMap = (
  eventIds: string[] | undefined
): {
  issuedTicketMap: Record<string, IssuedTicket>;
  error: Error | null;
  hasPendingWrites: boolean;
} => {
  const [issuedTicketsByEvent, setIssuedTicketsByEvent] = useState<
    Record<string, Record<string, IssuedTicket>>
  >({});
  const [error, setError] = useState<Error | null>(null);
  const [pendingByEvent, setPendingByEvent] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (!eventIds || eventIds.length === 0) {
      setIssuedTicketsByEvent({});
      setPendingByEvent({});
      setError(null);
      return;
    }

    setError(null);
    setIssuedTicketsByEvent({});
    setPendingByEvent({});

    const unsubscribes = eventIds.map((eventId) =>
      dbFirestore.issuedTickets(eventId).onSnapshot(
        { includeMetadataChanges: true },
        (snapshot) => {
          setPendingByEvent((prev) => {
            const next = snapshot.metadata.hasPendingWrites;
            if (prev[eventId] === next) return prev;
            return { ...prev, [eventId]: next };
          });

          const changes = snapshot.docChanges();
          if (changes.length === 0) return;
          setIssuedTicketsByEvent((prev) => {
            const next = { ...(prev[eventId] ?? {}) };
            changes.forEach((change) => {
              if (change.type === "removed") {
                delete next[change.doc.id];
                return;
              }
              const data = change.doc.data();
              if (data === null) {
                delete next[change.doc.id];
                return;
              }
              next[change.doc.id] = data;
            });
            return { ...prev, [eventId]: next };
          });
        },
        (err) => {
          console.error(
            `Failed to listen issuedTickets (eventId=${eventId}):`,
            err
          );
          setPendingByEvent((prev) => {
            if (!prev[eventId]) return prev;
            return { ...prev, [eventId]: false };
          });
          setError(err);
        }
      )
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [eventIds]);

  const issuedTicketMap: Record<string, IssuedTicket> = Object.values(
    issuedTicketsByEvent
  ).reduce((acc, tickets) => ({ ...acc, ...tickets }), {});

  const hasPendingWrites = Object.values(pendingByEvent).some(Boolean);

  return { issuedTicketMap, error, hasPendingWrites };
};
